/**
 * @fileoverview The welcome's first screen after the ZIP code: something about the
 * gardener's own place at once, then the ways to start their garden.
 *
 * Under the place's name as Today's heading ("Sparta, TN"), a card with two
 * plain lines, before anything is added: the frost countdown, the Today
 * brief's own line in the owner's wording ("First frost most likely around
 * Nov 1 · as early as Oct 19"; the to-do rebuild, batch 5: the welcome no
 * longer works out the next frost itself), and this week ("This week:
 * chilly Wednesday night, 38°F."), the brief's own line too (weekLine,
 * services/today/weekLine; audit batch 4, A21: the welcome no longer judges
 * frost, chill or heat, so a night Today calls frost is said as frost). The
 * full frost ranges stay on Weather and Seasons.
 *
 * Then its own heading, "Your garden starts here", and two doors, a photo and
 * a typed (B96; B30; the order and landings are lib/startDoors):
 *   - A photo of anything (seed packets, a seed order, plants already growing):
 *     the photo picker, on Today (onPhotos; TodayPage owns it); the photos go to
 *     the tray, where the server's guess says what each is (packets are then read
 *     as packets), so no photo is refused (B80); its square shows both start
 *     photos fanned; a plan with no photo reading, or one not yet known, gets the
 *     hint that promises no name (anyPhotoHintNoAi; C77);
 *   - Type a plant: the add form, over Today (onAdd; TodayPage owns it, B30 rev 5).
 *
 * With neither line given (a garden with no plants: Today shows only the doors,
 * at once, B30 revision 2) the card is not drawn.
 *
 * Shown by Today while the welcome's first step is due (useFirstStep: until a
 * plant of the gardener's own is added).
 *
 * Where: Today (TodayPage), in place of the jobs while the welcome's first
 * step is due.
 * API: none of its own; the frost and week lines come from Today (GET /today/brief),
 * and whether the plan reads photos from useAiAccess (GET /billing/status); the
 * doors' work (photo picker, add form) is TodayPage's.
 * Settings: none (the doors and their words are lib/startDoors and welcome.place.*).
 * Words (en/es): welcome.place.* (startsHere, anyPhoto, anyPhotoHint,
 * anyPhotoHintNoAi, addPlant, addPlantHint); the two lines are the brief's.
 * Watch out: no door leaves Today; "Type a plant" opens the same add form the
 * Plants page uses, over Today; the photo doors stay on Today.
 *
 * @module welcome/WelcomePlace
 */

import React from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useAiAccess } from '@/features/billing/lib/useAiAccess';
import { START_DOORS, doorHintKey, aiKnownAllowed, type StartDoorId } from './lib/startDoors';
import type { BriefWelcomeWeekLine } from '@/shared/api/todayBrief';

/** What the screen is told: the frost line and the week, both the brief's; none for a garden with no plants. */
interface WelcomePlaceProps {
  /** The brief's frost countdown, in the owner's wording; null for none (a frost-free place, a frost night this week). */
  frost?: string | null;
  /** The brief's "This week" line (weekLine), or null with no forecast. */
  week?: BriefWelcomeWeekLine | null;
  /** Opens the photo picker (the photo door); Today owns it so its sheet survives the first plant, and the photos go to the tray. */
  onPhotos: () => void;
  /** Opens the add-a-plant form (the type door); Today owns it too, so saving the first plant keeps the gardener on Today. */
  onAdd: () => void;
}

/**
 * A sentence with one part of it in bold, where that part first appears; the
 * whole sentence plain when the part is not in it.
 *
 * @param text - The whole sentence.
 * @param bold - The part drawn bold.
 * @returns The sentence, with the part in bold when it is there.
 */
function withBold(text: string, bold: string): React.ReactNode {
  const at = bold ? text.indexOf(bold) : -1;
  if (at < 0) return text;
  return <>{text.slice(0, at)}<b>{bold}</b>{text.slice(at + bold.length)}</>;
}

/**
 * The small square at a door's left: the photos when it has them (one filling the square, or two fanned, each turned
 * a little, the front one edged in the card's colour so the pair reads in every theme), else the icon. Decorative,
 * since the title says it all (B96).
 *
 * @param props.icon - The icon, when there are no photos.
 * @param props.thumbs - The photos, paths under public/.
 */
const DoorSquare: React.FC<{ icon?: React.ReactNode; thumbs?: readonly string[] }> = ({ icon, thumbs }) => (
  <span className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted text-primary" aria-hidden="true">
    {thumbs && thumbs.length >= 2 ? (
      <>
        <img src={thumbs[0]} alt="" width={29} height={29} className="absolute left-0.5 top-0.5 h-[29px] w-[29px] -rotate-6 rounded-md object-cover" />
        <img src={thumbs[1]} alt="" width={29} height={29} className="absolute bottom-0.5 right-0.5 h-[29px] w-[29px] rotate-6 rounded-md object-cover ring-2 ring-card" />
      </>
    ) : thumbs && thumbs.length === 1 ? (
      <img src={thumbs[0]} alt="" width={40} height={40} className="h-full w-full object-cover" />
    ) : icon}
  </span>
);

/**
 * One of the two ways to start: its square (photos or an icon), what it is, and a line under it.
 *
 * @param props.icon - The icon, drawn in a small square (when there are no thumbs).
 * @param props.thumbs - Small photos drawn in the square instead of the icon; decorative, since the title says it all.
 * @param props.title - What the way is.
 * @param props.hint - A line under it.
 * @param props.first - The quickest way: drawn with the primary border.
 * @param props.onClick - Starts it.
 */
const Way: React.FC<{ icon?: React.ReactNode; thumbs?: readonly string[]; title: string; hint: string; first?: boolean; onClick: () => void }> = ({
  icon, thumbs, title, hint, first = false, onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex min-h-[56px] w-full items-center gap-3 rounded-lg border bg-card p-3 text-left transition-colors hover:bg-muted ${first ? 'border-2 border-primary' : ''}`}
  >
    <DoorSquare icon={icon} thumbs={thumbs} />
    <span className="min-w-0">
      <span className="block font-semibold leading-snug">{title}</span>
      {hint && <span className="block text-sm text-muted-foreground">{hint}</span>}
    </span>
  </button>
);

/** The icon of each door with no photo (only the type door; the photo door draws its thumbs). */
const DOOR_ICONS: Record<StartDoorId, React.ReactNode> = {
  photo: null,
  type: <Plus className="h-5 w-5" />,
};

/** The frost and week card, then "Your garden starts here" and the two doors (B96). */
export const WelcomePlace: React.FC<WelcomePlaceProps> = ({ frost = null, week = null, onPhotos, onAdd }) => {
  const { reactTranslate: t } = useTranslation();
  // A plan with no photo reading keeps a hint that does not promise a name (D70 F4); until access is known, the Free words show (C77).
  const aiAllowed = aiKnownAllowed(useAiAccess());

  return (
    <div className="space-y-4">
      {(frost || week) && (
        <div className="space-y-2 rounded-lg border bg-card p-4">
          {/* OWNER-SET WORDING (the brief's countdown): never reworded or shortened here. */}
          {frost && <p>{frost}</p>}
          {week && <p>{withBold(week.text, week.bold)}</p>}
        </div>
      )}

      <h2 className="font-grow text-2xl font-bold leading-tight">{t('welcome.place.startsHere')}</h2>
      <div className="space-y-2">
        {START_DOORS.map((door) => (
          <Way
            key={door.id}
            first={door.first}
            icon={DOOR_ICONS[door.id]}
            thumbs={door.thumbs}
            title={t(door.titleKey)}
            hint={t(doorHintKey(door, aiAllowed))}
            onClick={() => {
              const lands = door.lands;
              if (lands.kind === 'photoPicker') onPhotos();
              else if (lands.kind === 'addForm') onAdd();
            }}
          />
        ))}
      </div>
    </div>
  );
};
